import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import { compose } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@domestika-dev/prudencio/dist/atoms/Layout";
import LoaderModal from "../../../common/LoaderModal";
import {
  LiveFeedbackTitle,
  LiveFeedbackQuestions as LiveFeedbackQuestionsComponent,
  LiveFeedbackSendButton,
} from "../component";
import injectMultipleReducers from "../../../utils/reducers/injectMultipleReducers";
import injectSaga from "../../../utils/saga/injectSaga";
import {
  courseReducer,
  liveFeedbackAnswersReducer,
  liveFeedbackQuestionsReducer,
  userReducer,
} from "../reducer";
import saga from "../saga";
import {
  useCourseActions,
  useUserDomainActions,
  useLiveFeedbackAnswerDomainActions,
  useLiveFeedbackQuestionDomainActions,
} from "../customHooks";
import { isUserCourseOnwer } from "../utils/user";
import * as UserRoles from "../constants/roles";
import * as ReducerKeys from "../constants/reducerKeys";

export const LiveFeedbackQuestions = ({ courseId, liveSessionId, onCloseModal }) => {
  const dispatch = useDispatch();
  const { getCourse } = useCourseActions();
  const { getLiveFeedbackQuestions } = useLiveFeedbackQuestionDomainActions();
  const { sendLiveFeedbackAnswers } = useLiveFeedbackAnswerDomainActions();
  const { getCurrentWithoutRelationshipsUser } = useUserDomainActions({ dispatch });

  const [answers, setAnswers] = useState([]);
  const { loading: courseLoading, course } = useSelector((state) => state[ReducerKeys.COURSE]);

  const {
    loading: liveFeedbackAnswersLoading,
    answersSentSuccess,
    errorSaving,
  } = useSelector((state) => state[ReducerKeys.LIVE_FEEDBACK_ANSWERS]);

  const {
    loading: liveFeedbackQuestionsLoading,
    liveFeedbackQuestionsFetched,
    liveFeedbackQuestions,
  } = useSelector((state) => state[ReducerKeys.LIVE_FEEDBACK_QUESTIONS]);

  const { loadingUser: userLoading, user } = useSelector((state) => state[ReducerKeys.USER]);

  const getUserRole = () =>
    isUserCourseOnwer(course, user) ? UserRoles.TEACHER : UserRoles.STUDENT;

  const onAnswersChanged = (updatedAnswers) => {
    setAnswers(updatedAnswers);
  };

  const showLoaderModal = () =>
    courseLoading ||
    liveFeedbackAnswersLoading ||
    liveFeedbackQuestionsLoading ||
    userLoading ||
    !course?.id;

  const showQuestions = () => course?.id && user?.id && liveFeedbackQuestions?.length;

  const validAnswersLength = () => answers?.length === liveFeedbackQuestions?.length;

  const sendAnswers = () => {
    if (!validAnswersLength()) {
      return;
    }

    sendLiveFeedbackAnswers(liveSessionId, answers);
  };

  useEffect(() => {
    getCurrentWithoutRelationshipsUser();
    getCourse(courseId);
    getLiveFeedbackQuestions(liveSessionId);
  }, []);

  useEffect(() => {
    if (liveFeedbackQuestionsFetched && !liveFeedbackQuestions?.length) {
      onCloseModal();
    }
  }, [liveFeedbackQuestionsFetched]);

  useEffect(() => {
    if (answersSentSuccess || errorSaving) {
      onCloseModal();
    }
  }, [answersSentSuccess, errorSaving]);

  useEffect(() => {
    if (showQuestions()) {
      const modalTarget = document.querySelector(".js-modal.live-feedback-questions");
      modalTarget?.classList?.remove("d-none");
    }
  }, [showQuestions]);

  return (
    <>
      <LoaderModal isOpen={showLoaderModal()} messageKey="live_sessions.loading_message" />
      {showQuestions() && (
        <Grid
          gridRowGap={[4, 4, 4]}
          gridTemplateColumns="repeat(2, minmax(200px, 1fr))"
          style={{ justifyItems: "center" }}
        >
          <LiveFeedbackTitle userRole={getUserRole()} />
          <LiveFeedbackQuestionsComponent
            liveFeedbackQuestions={liveFeedbackQuestions}
            onAnswersChanged={onAnswersChanged}
          />
          <LiveFeedbackSendButton onSendAnswers={sendAnswers} disabled={!validAnswersLength()} />
        </Grid>
      )}
    </>
  );
};

const withReducers = injectMultipleReducers([
  { key: ReducerKeys.COURSE, reducer: courseReducer },
  { key: ReducerKeys.LIVE_FEEDBACK_ANSWERS, reducer: liveFeedbackAnswersReducer },
  { key: ReducerKeys.LIVE_FEEDBACK_QUESTIONS, reducer: liveFeedbackQuestionsReducer },
  { key: ReducerKeys.USER, reducer: userReducer },
]);

const withSaga = injectSaga({ key: "course[live]", saga });

export default compose(withReducers, withSaga)(injectIntl(LiveFeedbackQuestions));

LiveFeedbackQuestions.propTypes = {
  courseId: PropTypes.string.isRequired,
  liveSessionId: PropTypes.string.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};
